import { useEffect, useState } from 'react';

import { getUser } from '../../modules/contexts/Auth/RequireAuth';
import { EnumOption } from '../../modules/types/types';
import httpClient from '../httpClient';

// Busca Usuario por ID
export async function getUserData() {
  try {
    const user = getUser();
    const userID = user.sub;

    const response = await httpClient.get(`/api/logacoes/user/${userID}`);

    // Verifica se a resposta da API contém o array `logsAcoes`
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (Array.isArray(response.data.logsAcoes)) {
      return response.data.logsAcoes;
    } else {
      console.error('Estrutura de dados inesperada:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Erro ao obter lista de Logs Acoes:', error);
    throw new Error('Erro ao obter lista de Logs Acoes: ' + error);
  }
}

//Buscar todos os usuarios
export async function getAllUser(unidade?: number) {
  try {
    const url =
      unidade !== undefined
        ? `/api/users?unidadeCadastro=${unidade}`
        : `/api/users`;

    const response = await httpClient.get(url);
    console.log('Resposta da API getAllUser:', response.data);

    // Verifica se a resposta da API contém o array `users`
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (Array.isArray(response.data.users)) {
      return response.data.users;
    } else {
      console.error('Estrutura de dados inesperada:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Erro ao obter lista de usuários:', error);
    throw new Error('Erro ao obter lista de usuários: ' + error);
  }
}

export async function getUsuariosPorVinculo() {
  try {
    const response = await httpClient.get('/api/users/por-vinculo');

    /**
     * A API retorna:
     * {
     *   message: string,
     *   users: User[]
     * }
     */

    console.log('Resposta da API getUsuariosPorVinculo:', response.data);

    if (Array.isArray(response.data?.users)) {
      return response.data.users;
    }

    // fallback defensivo (caso backend evolua)
    if (Array.isArray(response.data)) {
      return response.data;
    }

    console.error('Estrutura de dados inesperada:', response.data);
    return [];
  } catch (error) {
    console.error('Erro ao obter usuários por vínculo:', error);
    throw new Error(
      'Erro ao obter lista de usuários conforme vínculo: ' + error,
    );
  }
}

//Busca Alocacao de Usuario por ID
export async function getUserAlocacao(fichaID: string) {
  try {
    const response = await httpClient.get(`/api/alocacaodaf/user/${fichaID}`);
    return response.data;
  } catch (error) {
    throw new Error('Erro ao obter dados do usuário: ' + error);
  }
}

//Busca Papel de Usuario por ID
export async function getUserPapel(fichaID: string) {
  try {
    const response = await httpClient.get(`/api/papelsisaf/user/${fichaID}`);
    return response.data;
  } catch (error) {
    throw new Error('Erro ao obter dados do usuário: ' + error);
  }
}

//Busca Permissao de Usuario por ID
export async function getUserPermissao(fichaID: string) {
  try {
    const response = await httpClient.get(
      `/api/permissoesmodulos/user/${fichaID}`,
    );
    return response.data;
  } catch (error) {
    throw new Error('Erro ao obter dados do usuário: ' + error);
  }
}

//Busca Papel Ativo do Usuário
export async function getUserPapelAtivo(user: string) {
  try {
    const response = await httpClient.get(`/api/papelsisaf/papelativo/${user}`);
    return response.data;
  } catch (error) {
    throw new Error('Erro ao obter dados do usuário: ' + error);
  }
}

//Busca Alocação Ativa do Usuário
export async function getUserAlocacaoAtiva(user: string) {
  try {
    const response = await httpClient.get(`/api/alocacaodaf/ativa/${user}`);

    return response.data;
  } catch (error) {
    throw new Error('Erro ao obter dados do usuário: ' + error);
  }
}

export const useUsuariosByUnidadeDaf = () => {
  const [registro, setRegistro] = useState<EnumOption[]>([]);

  useEffect(() => {
    const fetchRegistro = async () => {
      try {
        const response = await httpClient.get('/api/users');
        const options = response.data?.users
          ?.filter(
            (usuario: { id: number; usuarioVinculo: [] }) =>
              usuario.usuarioVinculo.length > 0 && usuario.id !== 1,
          )
          .map((usuario: { id: number; nomeCompleto: string }) => ({
            value: usuario.id.toString(),
            label: usuario.nomeCompleto,
          }));

        // Adiciona "Não informado" no início
        setRegistro([
          { value: '0', label: 'Não informado' },
          ...(options ?? []),
        ]);
      } catch (error) {
        console.error('Erro ao buscar registros', error);
      }
    };

    fetchRegistro();
  }, []);

  return registro;
};

export const useUsuariosMesmoSetorDaf = () => {
  const [registro, setRegistro] = useState<EnumOption[]>([]);
  useEffect(() => {
    const fetchRegistro = async () => {
      try {
        const response = await httpClient.get(
          '/api/users/usuarios-mesmo-setor',
        );

        const options =
          response.data?.map(
            (usuario: { id: number; nomeCompleto: string }) => ({
              value: usuario.id.toString(),
              label: usuario.nomeCompleto,
            }),
          ) ?? [];

        // console.log('Opções de usuários do mesmo Setor DAF:', options);

        setRegistro([{ value: '0', label: 'Não informado' }, ...options]);
      } catch (error) {
        console.error('Erro ao buscar usuários do mesmo Setor DAF', error);
        setRegistro([{ value: '0', label: 'Não informado' }]);
      }
    };

    fetchRegistro();
  }, []);

  return registro;
};

export const useListUserServidores = () => {
  const [registro, setRegistro] = useState<EnumOption[]>([
    { value: '0', label: 'Não informado' },
  ]);

  useEffect(() => {
    const fetchRegistro = async () => {
      try {
        const response = await httpClient.get('/api/users/servidores/options');

        const data = Array.isArray(response.data) ? response.data : [];

        const options: EnumOption[] = data.map(
          (item: { value: number | string; label: string }) => ({
            value: String(item.value),
            label: item.label,
          }),
        );

        // garante 0 no início e remove duplicados (se backend já trouxe)
        const unique = options.filter(
          (opt, index, arr) =>
            index === arr.findIndex((x) => x.value === opt.value),
        );

        const finalOptions =
          unique.length > 0 && unique[0].value === '0'
            ? unique
            : [{ value: '0', label: 'Não informado' }, ...unique];

        setRegistro(finalOptions);
      } catch (error) {
        console.error('Erro ao buscar usuários servidores', error);
        setRegistro([{ value: '0', label: 'Não informado' }]);
      }
    };

    fetchRegistro();
  }, []);

  return registro;
};
